import { type FC, useState, useEffect } from "react";
import { Box, Button, IconButton, Alert, useTheme, alpha } from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import StyledTextField from "../Fields/Textfield";

interface KeyValuePair {
  key: string;
  value: string;
}

interface KeyValueEditorProps {
  value?: Record<string, unknown>;
  onChange?: (data: Record<string, unknown>) => void;
  label?: string;
  placeholder?: string;
  readOnly?: boolean;
}

const KeyValueEditor: FC<KeyValueEditorProps> = ({
  value = {},
  onChange,
  label,
  placeholder,
  readOnly = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const resolvedLabel = label ?? t("components.keyValueEditor.defaultLabel");
  const resolvedPlaceholder = placeholder ?? t("components.keyValueEditor.valuePlaceholder");

  // Convert object to array of key-value pairs
  const [pairs, setPairs] = useState<KeyValuePair[]>(() => {
    const entries = Object.entries(value);
    return entries.length > 0
      ? entries.map(([key, val]) => ({
          key,
          value: typeof val === "object" ? JSON.stringify(val) : String(val),
        }))
      : [{ key: "", value: "" }];
  });

  // Sync with value prop when it changes from parent
  useEffect(() => {
    const entries = Object.entries(value);
    if (entries.length > 0) {
      setPairs(
        entries.map(([key, val]) => ({
          key,
          value: typeof val === "object" ? JSON.stringify(val) : String(val),
        }))
      );
    }
  }, [value]);

  const handleAddRow = () => {
    const newPairs = [...pairs, { key: "", value: "" }];
    setPairs(newPairs);
  };

  const handleRemoveRow = (index: number) => {
    const newPairs = pairs.filter((_, i) => i !== index);
    setPairs(newPairs.length > 0 ? newPairs : [{ key: "", value: "" }]);
    updateParent(newPairs.length > 0 ? newPairs : [{ key: "", value: "" }]);
  };

  const handleKeyChange = (index: number, newKey: string) => {
    const newPairs = [...pairs];
    newPairs[index].key = newKey;
    setPairs(newPairs);
    updateParent(newPairs);
  };

  const handleValueChange = (index: number, newValue: string) => {
    const newPairs = [...pairs];
    newPairs[index].value = newValue;
    setPairs(newPairs);
    updateParent(newPairs);
  };

  const updateParent = (currentPairs: KeyValuePair[]) => {
    const obj: Record<string, unknown> = {};
    currentPairs.forEach((pair) => {
      if (pair.key.trim()) {
        // Try to parse value as JSON, otherwise use as string
        try {
          obj[pair.key.trim()] = JSON.parse(pair.value);
        } catch {
          obj[pair.key.trim()] = pair.value;
        }
      }
    });
    onChange?.(obj);
  };

  return (
    <Box>
      <Alert
        severity="info"
        sx={{ mb: 2, backgroundColor: alpha(theme.palette.info.main, 0.1) }}
      >
        {resolvedLabel}
      </Alert>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {pairs.map((pair, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
            }}
          >
            <StyledTextField
              label={t("components.keyValueEditor.keyLabel")}
              value={pair.key}
              onChange={(e) => handleKeyChange(index, e.target.value)}
              placeholder={t("components.keyValueEditor.keyPlaceholder")}
              mode="light"
              sx={{ flex: 1 }}
              disabled={readOnly}
              InputProps={{
                readOnly: readOnly,
              }}
            />
            <StyledTextField
              label={t("components.keyValueEditor.valueLabel")}
              value={pair.value}
              onChange={(e) => handleValueChange(index, e.target.value)}
              placeholder={resolvedPlaceholder}
              mode="light"
              sx={{ flex: 2 }}
              disabled={readOnly}
              InputProps={{
                readOnly: readOnly,
              }}
            />
            {!readOnly && pairs.length > 1 && (
              <IconButton
                onClick={() => handleRemoveRow(index)}
                color="error"
                sx={{
                  mt: 1,
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.error.main, 0.2),
                  },
                }}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        ))}
      </Box>

      {!readOnly && (
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddRow}
          variant="outlined"
          sx={{
            mt: 2,
            borderRadius: 2,
            textTransform: "none",
            borderColor: alpha(theme.palette.primary.main, 0.3),
            color: theme.palette.primary.main,
            "&:hover": {
              borderColor: theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
            },
          }}
        >
          {t("components.keyValueEditor.addRow")}
        </Button>
      )}
    </Box>
  );
};

export default KeyValueEditor;
